import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Calculator,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCurrency } from '@/lib/format';
import { formatStoreTime, fromInputValue, toInputValue } from '@/lib/store-time';
import { cn } from '@/lib/utils';
import {
  difference,
  useAddCheckInComment,
  useCheckInComments,
  useCheckInMovements,
  useCreateMovement,
  useDeleteMovement,
  useRecalculateCheckIn,
  useUpdateCheckIn,
  useUpdateMovement,
  type CashCheckIn,
  type CashMovement,
} from './cash-drawer.api';

/**
 * One session on one till.
 *
 * Three things a manager does with a drawer that does not balance: correct the
 * count, look at what actually moved through it, and leave a note for whoever
 * asks next. Those are the three tabs, rather than the three separate modals
 * the original opened from three separate buttons in a table cell.
 */
export function CheckInSheet({
  checkIn,
  open,
  onOpenChange,
}: {
  checkIn: CashCheckIn | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const update = useUpdateCheckIn();
  const recalculate = useRecalculateCheckIn();

  const [draft, setDraft] = useState({
    startTime: '',
    endTime: '',
    startAmount: 0,
    endAmount: '',
  });
  const [hydrated, setHydrated] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  if (checkIn && hydrated !== checkIn.id) {
    setHydrated(checkIn.id);
    setPreview(null);
    setDraft({
      startTime: toInputValue(checkIn.startTime),
      endTime: toInputValue(checkIn.endTime),
      startAmount: checkIn.startAmount,
      endAmount: checkIn.endAmount == null ? '' : String(checkIn.endAmount),
    });
  }

  if (!checkIn) return null;

  const set = (patch: Partial<typeof draft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const failed = () => toast.error(t('cashDrawer.actionFailed'));
  const gap = difference(checkIn);
  const isOpen = !checkIn.endTime;

  function save() {
    update.mutate(
      {
        id: checkIn!.id,
        startTime: fromInputValue(draft.startTime)!,
        endTime: fromInputValue(draft.endTime),
        startAmount: draft.startAmount,
        endAmount: draft.endAmount === '' ? null : Number(draft.endAmount),
      },
      {
        onSuccess: () => {
          toast.success(t('cashDrawer.saved'));
          onOpenChange(false);
        },
        onError: failed,
      }
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle className="truncate">
            {checkIn.user?.completeName ?? t('cashDrawer.unknownUser')}
          </SheetTitle>
          <p className="text-muted-foreground truncate text-sm">
            {checkIn.posLane?.tag ?? t('cashDrawer.unknownLane')} ·{' '}
            {formatStoreTime(checkIn.startTime)}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant={isOpen ? 'warning' : 'secondary'}>
              {isOpen ? t('cashDrawer.open') : t('cashDrawer.closed')}
            </Badge>
            {gap !== null && gap !== 0 && (
              <Badge variant={gap > 0 ? 'success' : 'destructive'}>
                {gap > 0 ? t('cashDrawer.over') : t('cashDrawer.under')}{' '}
                {formatCurrency(Math.abs(gap))}
              </Badge>
            )}
          </div>
        </SheetHeader>

        <div className="space-y-3 p-4">
          <Tabs defaultValue="count">
            <TabsList className="w-full">
              <TabsTrigger value="count" className="flex-1">
                {t('cashDrawer.theCount')}
              </TabsTrigger>
              <TabsTrigger value="movements" className="flex-1">
                {t('cashDrawer.movements')}
              </TabsTrigger>
              <TabsTrigger value="comments" className="flex-1">
                {t('cashDrawer.comments')}
                {checkIn.commentsCount > 0 && (
                  <Badge variant="secondary">{checkIn.commentsCount}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* ----------------------------------------------- the count */}
            <TabsContent value="count" className="space-y-3">
              {/* What the till worked out, before anything is corrected. */}
              <div className="divide-y rounded-xl border">
                <Figure
                  label={t('cashDrawer.sales')}
                  value={checkIn.profit}
                />
                <Figure
                  label={t('cashDrawer.cashIn')}
                  value={checkIn.totalReceived}
                />
                <Figure
                  label={t('cashDrawer.cashOut')}
                  value={checkIn.totalReturned}
                />
                <Figure
                  label={t('cashDrawer.drops')}
                  value={checkIn.dropInTotal}
                />
                <Figure
                  label={t('cashDrawer.expected')}
                  value={checkIn.calculatedEndAmount}
                  strong
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>{t('cashDrawer.startTime')}</Label>
                  <Input
                    type="datetime-local"
                    value={draft.startTime}
                    onChange={(event) => set({ startTime: event.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('cashDrawer.endTime')}</Label>
                  <Input
                    type="datetime-local"
                    value={draft.endTime}
                    onChange={(event) => set({ endTime: event.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('cashDrawer.openingAmount')}</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    value={draft.startAmount}
                    onChange={(event) =>
                      set({ startAmount: Number(event.target.value) })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('cashDrawer.closingAmount')}</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    placeholder={t('cashDrawer.notCounted')}
                    value={draft.endAmount}
                    onChange={(event) => set({ endAmount: event.target.value })}
                  />
                </div>
              </div>

              {/* Preview first: it says what would change without changing it. */}
              <div className="bg-muted/40 space-y-2 rounded-xl border p-3">
                <div className="flex items-center gap-2">
                  <Calculator className="text-muted-foreground size-4" />
                  <p className="text-sm font-medium">
                    {t('cashDrawer.recalculate')}
                  </p>
                </div>
                <p className="text-muted-foreground text-xs">
                  {t('cashDrawer.recalculateHint')}
                </p>
                {preview && <p className="text-sm">{preview}</p>}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-background"
                    disabled={recalculate.isPending}
                    onClick={() =>
                      recalculate.mutate(
                        { checkInIds: [checkIn.id], previewOnly: true },
                        {
                          onSuccess: (result) =>
                            setPreview(
                              t('cashDrawer.previewResult', {
                                evaluated: result.evaluated,
                                changed: result.changed,
                              })
                            ),
                          onError: failed,
                        }
                      )
                    }
                  >
                    {recalculate.isPending && (
                      <Loader2 className="animate-spin" />
                    )}
                    {t('cashDrawer.preview')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-background"
                    disabled={recalculate.isPending}
                    onClick={() =>
                      recalculate.mutate(
                        { checkInIds: [checkIn.id], previewOnly: false },
                        {
                          onSuccess: () => {
                            toast.success(t('cashDrawer.recalculated'));
                            onOpenChange(false);
                          },
                          onError: failed,
                        }
                      )
                    }
                  >
                    {t('cashDrawer.applyRecalculation')}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  disabled={update.isPending}
                  onClick={() => onOpenChange(false)}
                >
                  {t('common.cancel')}
                </Button>
                <Button disabled={update.isPending} onClick={save}>
                  {update.isPending && <Loader2 className="animate-spin" />}
                  {t('common.save')}
                </Button>
              </div>
            </TabsContent>

            {/* ------------------------------------------------ movements */}
            <TabsContent value="movements">
              <MovementsTab checkInId={checkIn.id} />
            </TabsContent>

            {/* ------------------------------------------------- comments */}
            <TabsContent value="comments">
              <CommentsTab checkInId={checkIn.id} />
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Figure({
  label,
  value,
  strong,
}: {
  label: string;
  value: number | null;
  strong?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span
        className={cn(
          'tabular-nums',
          strong ? 'text-base font-semibold' : 'text-sm font-medium'
        )}
      >
        {value == null ? '—' : formatCurrency(value)}
      </span>
    </div>
  );
}

const BLANK_MOVEMENT = {
  received: '',
  returned: '',
  comments: '',
  isDropIn: false,
};

function MovementsTab({ checkInId }: { checkInId: string }) {
  const { t } = useTranslation();

  const movements = useCheckInMovements(checkInId);
  const create = useCreateMovement(checkInId);
  const update = useUpdateMovement(checkInId);
  const remove = useDeleteMovement(checkInId);

  const [editing, setEditing] = useState<CashMovement | 'new' | null>(null);
  const [draft, setDraft] = useState(BLANK_MOVEMENT);

  const rows = movements.data ?? [];
  const failed = () => toast.error(t('cashDrawer.actionFailed'));
  const busy = create.isPending || update.isPending || remove.isPending;
  // The list being re-read after a movement is added, changed or removed.
  const movementsBusy = movements.isFetching && !movements.isPending;

  function open(movement: CashMovement | 'new') {
    setEditing(movement);
    setDraft(
      movement === 'new'
        ? BLANK_MOVEMENT
        : {
            received: movement.received == null ? '' : String(movement.received),
            returned: movement.returned == null ? '' : String(movement.returned),
            comments: movement.comments ?? '',
            isDropIn: movement.isDropIn,
          }
    );
  }

  function save() {
    const body = {
      received: draft.received === '' ? null : Number(draft.received),
      returned: draft.returned === '' ? null : Number(draft.returned),
      comments: draft.comments || null,
      isDropIn: draft.isDropIn,
    };
    const done = {
      onSuccess: () => {
        toast.success(t('cashDrawer.saved'));
        setEditing(null);
      },
      onError: failed,
    };

    if (editing === 'new') create.mutate({ checkInId, ...body }, done);
    else if (editing) update.mutate({ id: editing.id, ...body }, done);
  }

  return (
    <div className="space-y-3">
      <Button variant="outline" className="w-full" onClick={() => open('new')}>
        <Plus className="size-4" />
        {t('cashDrawer.addMovement')}
      </Button>

      {editing && (
        <div className="space-y-3 rounded-xl border p-3">
          <p className="text-sm font-medium">
            {editing === 'new'
              ? t('cashDrawer.addMovement')
              : t('cashDrawer.editMovement')}
          </p>

          <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
            <span className="min-w-0">
              <span className="block text-sm">{t('cashDrawer.isDropIn')}</span>
              <span className="text-muted-foreground block text-xs">
                {t('cashDrawer.isDropInHint')}
              </span>
            </span>
            <Switch
              checked={draft.isDropIn}
              onCheckedChange={(isDropIn) =>
                setDraft((current) => ({ ...current, isDropIn }))
              }
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('cashDrawer.cashIn')}</Label>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={draft.received}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    received: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('cashDrawer.cashOut')}</Label>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={draft.returned}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    returned: event.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('cashDrawer.comments')}</Label>
            <Input
              value={draft.comments}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  comments: event.target.value,
                }))
              }
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => setEditing(null)}
            >
              {t('common.cancel')}
            </Button>
            <Button size="sm" disabled={busy} onClick={save}>
              {(create.isPending || update.isPending) && (
                <Loader2 className="animate-spin" />
              )}
              {t('common.save')}
            </Button>
          </div>
        </div>
      )}

      {movements.isPending && <ListSkeleton />}
      {movements.isSuccess && rows.length === 0 && (
        <Empty busy={movementsBusy}>{t('cashDrawer.noMovements')}</Empty>
      )}

      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={movementsBusy} />
          <div
            aria-busy={movementsBusy}
            className={cn(
              'divide-y rounded-xl border',
              movementsBusy && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((movement) => (
              <div key={movement.id} className="flex items-start gap-3 p-3">
                {movement.received ? (
                  <ArrowDownLeft className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                ) : (
                  <ArrowUpRight className="text-destructive mt-0.5 size-4 shrink-0" />
                )}

                <button
                  type="button"
                  onClick={() => open(movement)}
                  className="min-w-0 flex-1 text-left hover:underline"
                >
                  <p className="text-sm font-medium tabular-nums">
                    {movement.received
                      ? formatCurrency(movement.received)
                      : formatCurrency(movement.returned ?? 0)}
                    {movement.isDropIn && (
                      <Badge variant="secondary" className="ml-2">
                        {t('cashDrawer.drop')}
                      </Badge>
                    )}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">
                    {formatStoreTime(movement.createdAt)}
                    {movement.order?.confirmationCode
                      && ` · ${movement.order.confirmationCode}`}
                  </p>
                  {movement.comments && (
                    <p className="text-muted-foreground truncate text-xs">
                      {movement.comments}
                    </p>
                  )}
                </button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('cashDrawer.delete')}
                      disabled={busy}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('cashDrawer.delete')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('cashDrawer.deletePrompt')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() =>
                          remove.mutate(movement.id, {
                            onSuccess: () =>
                              toast.success(t('cashDrawer.deleted')),
                            onError: failed,
                          })
                        }
                      >
                        {t('common.yes')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CommentsTab({ checkInId }: { checkInId: string }) {
  const { t } = useTranslation();
  const comments = useCheckInComments(checkInId);
  const add = useAddCheckInComment(checkInId);
  const [text, setText] = useState('');

  const rows = comments.data ?? [];
  // The thread being re-read after a comment is added.
  const commentsBusy = comments.isFetching && !comments.isPending;

  return (
    <div className="space-y-3">
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!text.trim()) return;
          add.mutate(text, {
            onSuccess: () => setText(''),
            onError: () => toast.error(t('cashDrawer.actionFailed')),
          });
        }}
      >
        <Input
          value={text}
          placeholder={t('cashDrawer.newComment')}
          onChange={(event) => setText(event.target.value)}
        />
        <Button type="submit" disabled={add.isPending || !text.trim()}>
          {add.isPending ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Plus className="size-4" />
          )}
        </Button>
      </form>

      {comments.isPending && <ListSkeleton />}
      {comments.isSuccess && rows.length === 0 && (
        <Empty busy={commentsBusy}>{t('cashDrawer.noComments')}</Empty>
      )}

      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={commentsBusy} />
          <div
            aria-busy={commentsBusy}
            className={cn(
              'divide-y rounded-xl border',
              commentsBusy && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((comment) => (
              <div key={comment.id} className="space-y-0.5 p-3">
                <p className="text-muted-foreground text-xs">
                  {comment.userCompleteName} · {formatStoreTime(comment.createdAt)}
                </p>
                <p className="text-sm">{comment.comments}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Stands in for a tab's list while it loads for the first time. */
function ListSkeleton() {
  return (
    <div className="divide-y rounded-xl border">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="flex items-start gap-3 p-3">
          <Skeleton className="mt-0.5 size-4 shrink-0 rounded" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-40" />
          </div>
        </div>
      ))}
    </div>
  );
}

function Empty({
  busy,
  children,
}: {
  /** The list is being re-read: it may be about to fill. */
  busy?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="relative rounded-xl">
      <BusyOverlay show={busy} />
      <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
        {children}
      </p>
    </div>
  );
}
